import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LoaderFunctionArgs, useLoaderData } from "react-router-dom"

import { Header } from "../components"
import { fetchAPI } from "../utils"

interface StateDivergenceDetail {
  queueName: string
  missingInRabbitCount: number
  missingInDbCount: number
  missingInRabbitIds: string[]
  missingInDbIds: string[]
  deepCheckedAtUtc?: string
}

interface LoaderResponse {
  queueName: string
  detail: StateDivergenceDetail | null
}

async function loader({ request }: LoaderFunctionArgs): Promise<LoaderResponse> {
  const url = new URL(request.url)
  const queueName = url.searchParams.get("queueName") ?? ""

  if (queueName === "") {
    return { queueName: "", detail: null }
  }

  const response = await fetchAPI<StateDivergenceDetail>(`/api/queues/${queueName}/state-divergence`)

  return {
    queueName,
    detail: response.ok ? response.result : null,
  }
}

const Route = () => {
  const { queueName, detail } = useLoaderData() as LoaderResponse

  return (
    <div className="flex min-h-screen w-full flex-col items-start justify-start gap-5 overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5 scrollbar-none">
      <div className="w-full space-y-5">
        <Header />
        <div className="flex items-center gap-3 flex-wrap">
          <FontAwesomeIcon icon={faCircleExclamation} className="text-base-content" />
          <h1 className="text-sm font-bold text-base-content">Queue State Divergence</h1>
          {queueName !== "" && <span className="font-mono text-xs text-base-content/60 break-all">{queueName}</span>}
          <button
            type="button"
            onClick={() => globalThis.history.back()}
            className="btn btn-outline btn-accent btn-xs ml-auto"
          >
            ← Back
          </button>
        </div>
      </div>
      <div className="divider" />

      {queueName === "" && <p className="text-sm text-base-content/60">No queue name provided.</p>}
      {queueName !== "" && detail === null && (
        <p className="text-sm text-base-content/60">No divergence detail found for this queue.</p>
      )}

      {detail != null && (
        <div className="w-full space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-accent bg-base-100 p-3">
              <div className="text-xs text-base-content/60">Missing In Rabbit</div>
              <div className="text-lg font-semibold text-base-content">{detail.missingInRabbitCount}</div>
            </div>
            <div className="rounded-lg border border-accent bg-base-100 p-3">
              <div className="text-xs text-base-content/60">Missing In DB</div>
              <div className="text-lg font-semibold text-base-content">{detail.missingInDbCount}</div>
            </div>
          </div>

          <div className="rounded-lg border border-accent bg-base-100 p-3 text-sm text-base-content space-y-1">
            <p>
              <span className="text-base-content/60">Deep check:</span> {detail.deepCheckedAtUtc ?? "N/A"}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section className="rounded-lg border border-accent bg-base-100 p-3">
              <h2 className="mb-2 text-sm font-semibold text-base-content">Missing In Rabbit IDs</h2>
              {detail.missingInRabbitIds.length === 0 ? (
                <p className="text-sm text-base-content/60">No IDs.</p>
              ) : (
                <ul className="space-y-1 text-xs font-mono text-base-content break-all max-h-[30rem] overflow-auto">
                  {detail.missingInRabbitIds.map((id) => (
                    <li key={`rabbit-${id}`}>{id}</li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-lg border border-accent bg-base-100 p-3">
              <h2 className="mb-2 text-sm font-semibold text-base-content">Missing In DB IDs</h2>
              {detail.missingInDbIds.length === 0 ? (
                <p className="text-sm text-base-content/60">No IDs.</p>
              ) : (
                <ul className="space-y-1 text-xs font-mono text-base-content break-all max-h-[30rem] overflow-auto">
                  {detail.missingInDbIds.map((id) => (
                    <li key={`db-${id}`}>{id}</li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  )
}

Route.loader = loader

export default Route
